import axios from 'axios'

import { store } from './app/store'
import { refreshToken } from './features/auth/api'
import tokenService from './services/token'

const setupAxiosInterceptors = (onUnauthenticated) => {
  const client = axios.create({
    baseURL: 'https://portal.janddfundraising.com/api',
    headers: {
      Accept: 'application/json',
      'Content-type': 'application/json'
    },
    responseType: 'json'
  })

  let isRefreshing = false
  let pendingRequests = []

  const processQueue = (error, token = null) => {
    pendingRequests.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })
    pendingRequests = []
  }

  client.interceptors.request.use(
    (config) => {
      const token = tokenService.accessToken
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  client.interceptors.response.use(
    (response) => {
      return response
    },
    async function (error) {
      const originalRequest = error.config

      if (!error.response) {
        return Promise.reject(error)
      }

      const { status } = error.response

      if (status !== 401 && status !== 403) {
        return Promise.reject(error)
      }

      // If the refresh request itself failed, log out
      if (originalRequest.url.includes('/auth/refresh')) {
        processQueue(error, null)
        isRefreshing = false
        tokenService.logout()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      // If already retried this request, give up
      if (originalRequest._retry) {
        return Promise.reject(error)
      }

      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`
          return client(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      if (
        tokenService.refreshToken &&
        tokenService.refreshToken !== 'undefined'
      ) {
        try {
          await onUnauthenticated()
          const newToken = tokenService.accessToken
          processQueue(null, newToken)
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`
          return client(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError, null)
          tokenService.logout()
          window.location.href = '/login'
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      tokenService.logout()
      window.location.href = '/login'
      return Promise.reject(error)
    }
  )

  return client
}

const apiClient = setupAxiosInterceptors(() =>
  store.dispatch(refreshToken(tokenService.refreshToken))
)

export default apiClient
